import { useEffect, useState } from "react";
import { authAxios } from "../utils/auth";
import { toast } from "sonner";
import { RefreshCw, Send, PlayCircle, RotateCcw, AlertTriangle, CheckCircle2, XCircle, Clock } from "lucide-react";
import AdminMenu from "../components/AdminMenu";

const cents = (c) => (c == null ? "—" : `$${(Number(c) / 100).toFixed(2)}`);

const STATUS_STYLE = {
  SCHEDULED: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  EXECUTING: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  COMPLETED: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  PARTIAL:   "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  PAID:      "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  FAILED:    "bg-red-500/15 text-red-300 ring-red-500/30",
};

const ITEM_ICON = { PAID: CheckCircle2, FAILED: XCircle, SCHEDULED: Clock };

export default function PayoutBatchesPage() {
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [openBatch, setOpenBatch] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([
        authAxios.get("/admin/payouts/preview"),
        authAxios.get("/admin/payouts/batches?limit=25"),
      ]);
      setPreview(p.data);
      setBatches(b.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (!preview?.eligible_count) {
      toast.warning("No eligible users to pay out");
      return;
    }
    if (!window.confirm(
      `Run payout for ${preview.eligible_count} user(s) totalling ${cents(preview.total_cents)}?`
    )) return;
    setRunning(true);
    try {
      const res = await authAxios.post("/admin/payouts/run");
      toast.success(`Batch ${res.data?.batch_id?.slice(-6)}: ${res.data?.status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    } finally {
      setRunning(false);
    }
  };

  const retryBatch = async (batchId) => {
    try {
      const res = await authAxios.post(`/admin/payouts/batches/${batchId}/retry`);
      toast.success(res.data?.note || `Retried ${res.data?.retried || 0} item(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white/80 p-4 sm:p-6" data-testid="payout-batches-page">
      <header className="max-w-[1400px] mx-auto mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Send className="w-5 h-5 text-[#C5A55A]" />
          <div>
            <h1 className="text-lg font-semibold text-white">Payout Batches</h1>
            <p className="text-[11px] text-white/40">SIMPLE_2026_V1 · Stripe Connect · {preview?.is_sandbox ? "SANDBOX (manual)" : "PRODUCTION"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runNow} disabled={running || !preview?.eligible_count}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="run-payout-btn">
            <PlayCircle className={`w-3.5 h-3.5 ${running ? "animate-pulse" : ""}`} />
            {running ? "Running…" : "Run payout now"}
          </button>
          <button onClick={load} disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 border border-[#C5A55A]/30 text-[#C5A55A] disabled:opacity-50"
                  data-testid="refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <AdminMenu />
        </div>
      </header>

      {/* Preview */}
      <section className="max-w-[1400px] mx-auto rounded-xl border border-white/[0.06] bg-[#111113] p-5 mb-6" data-testid="preview-section">
        <h2 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">Next batch preview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Stat label="Eligible users" value={preview?.eligible_count ?? "—"} testId="preview-eligible" />
          <Stat label="Total to pay" value={cents(preview?.total_cents)} testId="preview-total" />
          <Stat label="Excluded" value={preview?.excluded_count ?? "—"} testId="preview-excluded" />
          <Stat label="Minimum payout" value={cents(preview?.minimum_cents)} testId="preview-minimum" />
        </div>

        {preview?.eligible?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="eligible-table">
              <thead className="text-white/40 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left py-1.5 pr-3 font-medium">User</th>
                  <th className="text-left py-1.5 pr-3 font-medium">Stripe Connect</th>
                  <th className="text-right py-1.5 pr-3 font-medium">Rows</th>
                  <th className="text-right py-1.5 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {preview.eligible.map((u) => (
                  <tr key={u.user_id} className="border-t border-white/[0.04]">
                    <td className="py-1.5 pr-3">
                      <div className="text-white/80">{u.name || u.email}</div>
                      <div className="font-mono text-[10px] text-white/30">{u.user_id}</div>
                    </td>
                    <td className="py-1.5 pr-3 font-mono text-[10px] text-white/50">{u.stripe_account_id || "—"}</td>
                    <td className="py-1.5 pr-3 text-right text-white/60">{u.row_count}</td>
                    <td className="py-1.5 text-right font-mono text-emerald-300 font-medium">{cents(u.available_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-white/30 text-sm py-3">No eligible users at the current minimum payout threshold.</div>
        )}

        {preview?.excluded?.length ? (
          <details className="mt-4 text-xs">
            <summary className="cursor-pointer text-white/40 hover:text-white/60 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> {preview.excluded.length} excluded (click to expand)
            </summary>
            <ul className="mt-2 space-y-1 pl-5">
              {preview.excluded.map((u) => (
                <li key={u.user_id} className="text-white/50">
                  <span className="text-white/70">{u.name || u.email || u.user_id}</span>
                  <span className="text-white/30 mx-2">·</span>
                  <span className="font-mono text-amber-400/70">{u.reason}</span>
                  <span className="text-white/30 mx-2">·</span>
                  <span className="text-white/50">{cents(u.available_cents)}</span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </section>

      {/* History */}
      <section className="max-w-[1400px] mx-auto rounded-xl border border-white/[0.06] bg-[#111113] overflow-hidden" data-testid="history-section">
        <div className="px-5 py-3 border-b border-white/[0.06]">
          <h2 className="text-sm font-semibold text-white/60 uppercase tracking-wider">Batch history</h2>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-white/[0.02] text-white/40 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left py-2 px-3 font-medium">Created</th>
              <th className="text-left py-2 px-3 font-medium">Batch</th>
              <th className="text-right py-2 px-3 font-medium">Users</th>
              <th className="text-right py-2 px-3 font-medium">Reserved</th>
              <th className="text-right py-2 px-3 font-medium">Paid</th>
              <th className="text-left py-2 px-3 font-medium">Status</th>
              <th className="text-left py-2 px-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {batches.length === 0 && (
              <tr><td colSpan="7" className="text-center py-8 text-white/30">No batches yet — hit "Run payout now" once eligible users appear.</td></tr>
            )}
            {batches.map((b) => (
              <>
                <tr key={b.id} className="border-t border-white/[0.04] hover:bg-white/[0.02]" data-testid={`batch-row-${b.id}`}>
                  <td className="py-1.5 px-3 text-white/60 font-mono text-[10px]">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</td>
                  <td className="py-1.5 px-3 font-mono text-[10px] text-white/60">{b.id}</td>
                  <td className="py-1.5 px-3 text-right text-white/70">{b.items?.length ?? 0}</td>
                  <td className="py-1.5 px-3 text-right font-mono text-white/70">{cents(b.total_reserved_cents)}</td>
                  <td className="py-1.5 px-3 text-right font-mono text-emerald-300">{cents(b.total_paid_cents)}</td>
                  <td className="py-1.5 px-3">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ${STATUS_STYLE[b.status] || ""}`}>
                      {b.status}
                    </span>
                    {b.is_sandbox && <span className="ml-2 text-[10px] text-white/30 font-mono">sandbox</span>}
                  </td>
                  <td className="py-1.5 px-3">
                    <button onClick={() => setOpenBatch(openBatch === b.id ? null : b.id)}
                            className="text-[10px] text-[#C5A55A]/70 hover:text-[#C5A55A]">
                      {openBatch === b.id ? "Hide" : "Details"}
                    </button>
                    {b.status === "PARTIAL" && (
                      <button onClick={() => retryBatch(b.id)}
                              className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200"
                              data-testid={`retry-btn-${b.id}`}>
                        <RotateCcw className="w-3 h-3" /> Retry failed
                      </button>
                    )}
                  </td>
                </tr>
                {openBatch === b.id && (
                  <tr className="bg-white/[0.02]">
                    <td colSpan="7" className="px-5 py-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {b.items?.map((it) => {
                          const Icon = ITEM_ICON[it.status] || Clock;
                          return (
                            <div key={it.user_id} className="flex items-start gap-2 py-1 text-[11px] border-l-2 border-white/10 pl-2">
                              <Icon className={`w-3.5 h-3.5 mt-0.5 ${it.status === "PAID" ? "text-emerald-400" : it.status === "FAILED" ? "text-red-400" : "text-sky-400"}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between gap-2">
                                  <span className="text-white/80 truncate">{it.name || it.email || it.user_id}</span>
                                  <span className="font-mono text-emerald-300">{cents(it.reserved_cents)}</span>
                                </div>
                                <div className="text-white/40 font-mono text-[10px]">
                                  {it.status}
                                  {it.stripe_transfer_id && <span> · {it.stripe_transfer_id}</span>}
                                  {it.error && <span className="text-red-400"> · {it.error}</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-lg bg-[#0a0a0a] ring-1 ring-white/[0.06] p-3" data-testid={testId}>
      <div className="text-[10px] text-white/40 uppercase tracking-wider">{label}</div>
      <div className="text-lg font-mono text-white/90 mt-1">{value}</div>
    </div>
  );
}
